import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock,
  LogOut,
  Mail,
  MessageCircle,
  Phone,
  RefreshCw,
  User,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue, réessayez.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return iso;
  }
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin_token"));
  const [password, setPassword] = useState("");
  const [tab, setTab] = useState("messages");
  const [messages, setMessages] = useState([]);
  const [chats, setChats] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]');
    if (meta) meta.setAttribute("content", "noindex, nofollow");
  }, []);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${token || ""}` },
  });

  const load = async () => {
    setLoading(true);
    try {
      const [m, c, s] = await Promise.all([
        axios.get(`${API}/admin/messages`, authHeaders()),
        axios.get(`${API}/admin/chats`, authHeaders()),
        axios.get(`${API}/admin/stats`, authHeaders()),
      ]);
      setMessages(m.data.messages || []);
      setChats(c.data.chats || []);
      setStats(s.data || null);
    } catch (e) {
      if (e.response?.status === 401) {
        sessionStorage.removeItem("admin_token");
        setToken(null);
        toast.error("Session expirée, reconnectez-vous.");
      } else {
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin_token", data.token);
      setToken(data.token);
      toast.success("Bienvenue !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const logout = () => {
    sessionStorage.removeItem("admin_token");
    setToken(null);
    setMessages([]);
    setChats([]);
  };

  if (!token) {
    return (
      <div className="flex min-h-[80vh] items-center justify-center bg-mist px-6 pt-24" data-testid="admin-login-view">
        <form
          onSubmit={login}
          className="w-full max-w-md rounded-[1.75rem] border border-black/5 bg-white p-9 shadow-[0_35px_80px_-30px_rgba(0,55,70,0.4)]"
          data-testid="admin-login-form"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white">
            <Lock size={20} />
          </span>
          <h1 className="mt-5 text-center text-2xl font-bold text-ink">
            Console d'administration
          </h1>
          <p className="mt-2 text-center text-sm text-ink/50">
            Accès réservé à l'équipe Syndicappli.
          </p>
          <label htmlFor="admin-password" className="mt-7 block text-sm font-medium text-ink/70">
            Mot de passe
          </label>
          <input
            id="admin-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="input-app mt-1.5"
            placeholder="••••••••"
            data-testid="admin-login-input"
          />
          <button
            type="submit"
            className="mt-6 flex w-full items-center justify-center gap-2.5 rounded-full bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] px-8 py-3.5 text-sm font-bold text-white shadow-[0_15px_35px_-12px_rgba(0,190,200,0.6)] transition-transform duration-300 hover:scale-[1.02] active:scale-95"
            data-testid="admin-login-button"
          >
            Se connecter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-mist pb-24 pt-28" data-testid="admin-console-view">
      <div className="mx-auto max-w-5xl px-6 lg:px-10">
        <div className="rounded-[1.75rem] border border-black/5 bg-white p-6 shadow-[0_25px_60px_-30px_rgba(45,52,54,0.4)] md:p-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-ink">Console d'administration</h1>
              <p className="text-sm text-ink/50">
                {messages.length} message{messages.length > 1 ? "s" : ""} de contact ·{" "}
                {chats.length} conversation{chats.length > 1 ? "s" : ""} IA
              </p>
            </div>
            <div className="flex items-center gap-2.5">
              <button
                onClick={load}
                className="flex items-center gap-2 rounded-full border border-ink/15 px-4 py-2 text-xs font-bold text-ink transition-colors hover:border-electric hover:text-electric"
                data-testid="admin-refresh-button"
              >
                <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
                Actualiser
              </button>
              <button
                onClick={logout}
                className="flex items-center gap-2 rounded-full bg-ink px-4 py-2 text-xs font-bold text-white"
                data-testid="admin-logout-button"
              >
                <LogOut size={14} />
                Déconnexion
              </button>
            </div>
          </div>

          <div className="mt-6 flex gap-2">
            {[
              { id: "messages", label: "Messages de contact", icon: Mail },
              { id: "chats", label: "Conversations IA", icon: MessageCircle },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold transition-colors ${
                  tab === t.id
                    ? "bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] text-white"
                    : "bg-mist text-ink/60 hover:text-ink"
                }`}
                data-testid={`admin-tab-${t.id}`}
              >
                <t.icon size={14} />
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "messages" && (
          <>
            {stats && (
              <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4" data-testid="admin-stats">
                {[
                  { label: "Demandes totales", value: stats.total, testid: "admin-stats-total" },
                  { label: "Syndics", value: stats.syndics, testid: "admin-stats-syndics" },
                  { label: "Copropriétaires", value: stats.coproprietaires, testid: "admin-stats-coproprietaires" },
                  { label: "Profil non précisé", value: stats.sans_profil, testid: "admin-stats-sans-profil" },
                ].map((s) => (
                  <div
                    key={s.testid}
                    data-testid={s.testid}
                    className="rounded-2xl border border-black/5 bg-white p-5 text-center shadow-[0_15px_40px_-25px_rgba(45,52,54,0.35)]"
                  >
                    <p className="text-3xl font-bold text-ink">{s.value}</p>
                    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-ink/40">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 grid gap-4" data-testid="admin-messages-list">
            {messages.length === 0 && (
              <p className="rounded-2xl bg-white p-6 text-center text-sm text-ink/40">
                Aucun message de contact pour le moment.
              </p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_15px_40px_-25px_rgba(45,52,54,0.35)]"
                data-testid="admin-message-card"
              >
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                  <span className="flex items-center gap-1.5 font-bold text-ink">
                    <User size={14} className="text-electric" />
                    {m.nom}
                  </span>
                  {m.profil && (
                    <span
                      className="rounded-full bg-electric/10 px-2.5 py-0.5 text-xs font-bold text-electric"
                      data-testid="admin-message-profil"
                    >
                      {m.profil}
                    </span>
                  )}
                  <a href={`mailto:${m.email}`} className="flex items-center gap-1.5 font-semibold text-electric">
                    <Mail size={14} />
                    {m.email}
                  </a>
                  {m.telephone && (
                    <a href={`tel:${m.telephone}`} className="flex items-center gap-1.5 font-semibold text-ink/60">
                      <Phone size={14} />
                      {m.telephone}
                    </a>
                  )}
                  <span className="ml-auto text-xs font-semibold text-ink/40">{fmt(m.created_at)}</span>
                </div>
                <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-ink/75">
                  {m.message}
                </p>
              </div>
            ))}
            </div>
          </>
        )}

        {tab === "chats" && (
          <div className="mt-6 grid gap-4" data-testid="admin-chats-list">
            {chats.length === 0 && (
              <p className="rounded-2xl bg-white p-6 text-center text-sm text-ink/40">
                Aucune conversation pour le moment.
              </p>
            )}
            {chats.map((c) => (
              <div
                key={c.session_id}
                className="rounded-3xl border border-black/5 bg-white p-6 shadow-[0_15px_40px_-25px_rgba(45,52,54,0.35)]"
                data-testid="admin-chat-session"
              >
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-black/5 pb-3">
                  <p className="font-mono text-xs font-semibold text-ink/50">
                    Session {c.session_id.slice(0, 14)}…
                  </p>
                  <p className="text-xs font-semibold text-ink/40">
                    {c.messages.length} échanges · {fmt(c.updated_at)}
                  </p>
                </div>
                <div className="mt-4 space-y-2.5">
                  {c.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <p
                        className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-snug ${
                          m.role === "user"
                            ? "rounded-tr-md bg-bubble text-ink"
                            : "rounded-tl-md border border-black/5 bg-mist text-ink"
                        }`}
                      >
                        {m.content}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
